import type { SrpTopRolesStripItem } from '../../types/srp-top-roles-strip';

export type TopRolesStripApiResponse = {
  roles: SrpTopRolesStripItem[];
};

/**
 * Browser: loads the same top-roles list as SSR when the page payload has no strip
 * (e.g. client-side navigation into the SRP).
 */
export async function fetchTopRolesStripFromApi(signal?: AbortSignal): Promise<SrpTopRolesStripItem[]> {
  try {
    const res = await fetch('/api/top-roles-strip', {
      method: 'GET',
      credentials: 'same-origin',
      signal,
    });
    if (!res.ok) return [];
    const json = (await res.json()) as TopRolesStripApiResponse;
    return Array.isArray(json?.roles) ? json.roles : [];
  } catch {
    return [];
  }
}

/** Session cache so repeat SRP visits and concurrent callers share one network request. */
let memoryCache: SrpTopRolesStripItem[] | null = null;
let inflight: Promise<SrpTopRolesStripItem[]> | null = null;

/**
 * Single-flight + memory cache for the top-roles strip. Prefer this over {@link fetchTopRolesStripFromApi}
 * for UI so navigations do not refetch or race duplicate requests.
 */
export function loadTopRolesStripDeduped(): Promise<SrpTopRolesStripItem[]> {
  if (memoryCache !== null) {
    return Promise.resolve(memoryCache);
  }
  if (inflight) {
    return inflight;
  }
  inflight = fetchTopRolesStripFromApi()
    .then((roles) => {
      memoryCache = roles;
      return roles;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}
